import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import en from "@/locales/en.json";
import fr from "@/locales/fr.json";
import ar from "@/locales/ar.json";

export const LOCALES = { en, fr, ar };
export const RTL_LOCALES = ["ar"];

const I18nContext = createContext(null);

function get(obj, path) {
  return path.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
}

export function I18nProvider({ children, initial = "en" }) {
  const [locale, setLocaleState] = useState(initial);

  const setLocale = useCallback((l) => {
    if (LOCALES[l]) setLocaleState(l);
  }, []);

  const dir = RTL_LOCALES.includes(locale) ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
    return () => {
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
    };
  }, [locale, dir]);

  const t = useCallback(
    (key, vars) => {
      let str = get(LOCALES[locale], key);
      if (str === undefined) str = get(LOCALES.en, key);
      if (str === undefined) return key;
      if (vars && typeof str === "string") {
        Object.entries(vars).forEach(([k, v]) => {
          str = str.replace(new RegExp(`{${k}}`, "g"), String(v));
        });
      }
      return str;
    },
    [locale]
  );

  const value = useMemo(() => ({ locale, setLocale, t, dir, isRTL: dir === "rtl" }), [locale, setLocale, t, dir]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
